import React, { useState, useCallback, useMemo } from 'react';

import { CheckOutlined, CheckCircleOutlined } from '@ant-design/icons';
import MultipleValue from '@components/shared/table/Rules/component/MultipleValue';
import TableIndex from '@components/shared/table/TableIndex';
import type { TableService, SupportRule } from '@components/shared/table/TableIndex';
import { Tabs, Button, Tag, Space, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { resolveNotification } from '../../../utils/notificationResolver';

interface NotificationServiceResult {
  success: boolean;
  message?: string;
  [key: string]: unknown;
}

interface NotificationService extends TableService {
  getRecords: (payload: Record<string, unknown>, scope?: string) => Promise<{ data: unknown[]; has_more: boolean }>;
  getIds: (payload: Record<string, unknown>, scope?: string) => Promise<{ data: string[] }>;
  setRead: (ids: string[]) => Promise<NotificationServiceResult>;
  readAll: () => Promise<NotificationServiceResult>;
}

interface NotificationRow {
  id: string;
  category?: string;
  created_at?: string;
  [key: string]: unknown;
}

interface NotificationListProps {
  service: NotificationService;
  role?: string;
}

/**
 * 通知列表組件（Shared）
 *
 * @param {String} role - 角色標識（'admin' 或 'operator'）
 */
const NotificationList: React.FC<NotificationListProps> = ({ service, role = 'admin' }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('unread');
  const [refreshKey, setRefreshKey] = useState(0);

  // Memoized service adapters for TableIndex
  const unreadTableService = useMemo<TableService>(() => ({
    getRecords: (params: Record<string, unknown>) => service.getRecords(params, 'unread'),
    getIds: (params: Record<string, unknown>) => service.getIds(params, 'unread'),
  }), [service]);

  const readTableService = useMemo<TableService>(() => ({
    getRecords: (params: Record<string, unknown>) => service.getRecords(params, 'read'),
    getIds: (params: Record<string, unknown>) => service.getIds(params, 'read'),
  }), [service]);

  // 分類選項
  const categoryOptions = useMemo(() => [
    { label: t('notifications.categories.system'), value: 'system' },
    { label: t('notifications.categories.order'), value: 'order' },
    { label: t('notifications.categories.inbound'), value: 'inbound' },
    { label: t('notifications.categories.stock'), value: 'stock' },
    { label: t('notifications.categories.platform'), value: 'platform' },
  ], [t]);

  // 分類標籤顏色映射
  const getCategoryColor = useCallback((category: string | undefined) => {
    const colorMap: Record<string, string> = {
      system: 'blue',
      order: 'green',
      inbound: 'cyan',
      stock: 'orange',
      platform: 'magenta'
    };
    return (category && colorMap[category]) || 'default';
  }, []);

  // 標記選中的通知為已讀
  const handleMarkAsRead = useCallback(async (selectedIds: Iterable<string> | ArrayLike<string>) => {
    const idsArray = Array.from(selectedIds);
    if (!idsArray || idsArray.length === 0) {
      message.warning(t('notifications.list.select_notifications'));
      return;
    }

    try {
      const result = await service.setRead(idsArray);

      if (result.success) {
        message.success(t('notifications.list.marked_as_read'));
        // 刷新列表
        setRefreshKey(prev => prev + 1);
      } else {
        message.error(result.message || t('common.error.operation_failed'));
      }
    } catch (error) {
      console.error('[NotificationList] Failed to mark as read:', error);
      message.error(t('common.error.operation_failed'));
    }
  }, [service, t]);

  // 一鍵標記所有未讀通知為已讀
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      const result = await service.readAll();

      if (result.success) {
        message.success(t('notifications.list.marked_as_read'));
        setRefreshKey(prev => prev + 1);
      } else {
        message.error(result.message || t('common.error.operation_failed'));
      }
    } catch (error) {
      console.error('[NotificationList] Failed to mark all as read:', error);
      message.error(t('common.error.operation_failed'));
    }
  }, [service, t]);

  // 截斷文字函數
  const truncateText = useCallback((text: string | undefined | null, maxLength = 100) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  }, []);

  // 共用列定義（unread / read 共用同一組 columns）
  const columns = useMemo<unknown[]>(() => [
    {
      nameKey: 'notifications.list.columns.title',
      persist: true,
      width: '25%',
      wrap: true,
      cell: (row: NotificationRow) => {
        const { title } = resolveNotification(row);
        return (
          <Link to={`/notifications/${row.id}`} style={{ color: 'inherit' }}>
            {title || '-'}
          </Link>
        );
      },
    },
    {
      nameKey: 'notifications.list.columns.content',
      persist: true,
      width: '35%',
      wrap: true,
      cell: (row: NotificationRow) => {
        const { description } = resolveNotification(row);
        if (!description) return '-';

        return (
          <Link
            to={`/notifications/${row.id}`}
            style={{ color: '#666', textDecoration: 'none' }}
          >
            {truncateText(description, 150)}
          </Link>
        );
      },
    },
    {
      nameKey: 'notifications.list.columns.category',
      persist: true,
      width: '120px',
      cell: ({ category }: NotificationRow) => (
        <Tag color={getCategoryColor(category)}>
          {t(`notifications.categories.${category}`)}
        </Tag>
      ),
    },
    {
      nameKey: 'common.created_at',
      persist: true,
      sortable: true,
      sortFieldName: 'notifications.created_at',
      width: '180px',
      selector: ({ created_at }: NotificationRow) => created_at || '-',
    },
  ], [t, getCategoryColor, truncateText]);

  // 篩選規則
  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('notifications.list.filters.category'),
      component: MultipleValue,
      inputName: 'notifications.category',
      defaultPinned: true,
      createable: false,
      defaultOptions: categoryOptions,
    },
  ], [t, categoryOptions]);

  // 未讀通知批量操作
  const unreadBatchActions = useMemo(() => [
    {
      key: 'mark_read',
      name: t('notifications.list.batch_actions.mark_read'),
      icon: <CheckOutlined />,
      action: handleMarkAsRead,
    },
  ], [t, handleMarkAsRead]);

  // batchActions 不在 TableIndexProps 中，但 runtime 仍需傳入。
  // 透過 spread 物件 cast 為 Record<string, unknown> 保留 prop，不對 <TableIndex> 做 cast。
  const unreadExtraProps = { batchActions: unreadBatchActions } as Record<string, unknown>;

  const tabItems = [
    {
      key: 'unread',
      label: t('notifications.list.tabs.unread'),
      children: (
        <TableIndex
          key={`unread-${refreshKey}`}
          service={unreadTableService}
          columns={columns}
          supportRules={supportRules}
          tableId="notifications_unread"
          columnsEditable={false}
          {...unreadExtraProps}
          defaultQuery={{
            sorting_field: 'notifications.created_at',
            sorting_direction: 'desc'
          }}
          filterHelpExample={t('notifications.list.filter_help_example') as string}
          filterDocsUrl={`/docs/${role}/notifications/list#features`}
          afterFilter={
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 0' }}>
              <Button
                icon={<CheckCircleOutlined />}
                onClick={handleMarkAllAsRead}
              >
                {t('notifications.list.mark_all_read')}
              </Button>
            </div>
          }
        />
      ),
    },
    {
      key: 'read',
      label: t('notifications.list.tabs.read'),
      children: (
        <TableIndex
          key={`read-${refreshKey}`}
          service={readTableService}
          columns={columns}
          supportRules={supportRules}
          tableId="notifications_read"
          columnsEditable={false}
          defaultQuery={{
            sorting_field: 'notifications.created_at',
            sorting_direction: 'desc'
          }}
          filterHelpExample={t('notifications.list.filter_help_example') as string}
          filterDocsUrl={`/docs/${role}/notifications/list#features`}
        />
      ),
    },
  ];

  return (
    <Tabs
      activeKey={activeTab}
      onChange={setActiveTab}
      items={tabItems}
    />
  );
};

export default NotificationList;
